import * as React from 'react';

import {
  Button,
  Checkbox,
  Col,
  Popover,
  PopoverTrigger,
  Row,
  SearchField,
  Tooltip,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { LucidePlusCircle, LucideXCircle } from 'lucide-react';

import { useAddUsersToRoleMutation } from './_graphql/useAddUsersToRoleMutation';
import { useUsersQuery } from './_graphql/useUsersQuery';

import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

interface AddUsersToRolePopoverProps {
  roleId: string;
  facilityId: string;
  existingUserIds: Set<string>;
}

export const AddUsersToRolePopover = ({
  roleId,
  facilityId,
  existingUserIds,
}: AddUsersToRolePopoverProps) => {
  const t = useTranslations();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedUserIds, setSelectedUserIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [isAdding, setIsAdding] = React.useState(false);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data: { users = [] } = {}, isLoading } = useUsersQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit: 1000,
    offset: 0,
  });

  const availableUsers = React.useMemo(
    () =>
      users.filter(
        (user) => !existingUserIds.has(user.id) && user.status !== 'archived',
      ),
    [users, existingUserIds],
  );

  const allUsersAdded =
    !isLoading && users.length > 0 && availableUsers.length === 0;

  const filteredUsers = React.useMemo(() => {
    if (!debouncedSearchTerm) return availableUsers;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(lowerSearch) ||
        user.email.toLowerCase().includes(lowerSearch),
    );
  }, [availableUsers, debouncedSearchTerm]);

  const { mutateAsync: addUsersToRole } = useAddUsersToRoleMutation();

  const selectedCount = selectedUserIds.size;

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleClear = () => {
    setSelectedUserIds(new Set());
  };

  const resetState = () => {
    setSearchTerm('');
    setSelectedUserIds(new Set());
    setIsAdding(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetState();
    }
    setIsOpen(open);
  };

  const handleDiscard = () => {
    setIsOpen(false);
    resetState();
  };

  const handleAddUsers = async () => {
    if (selectedCount === 0) return;

    setIsAdding(true);
    try {
      const userIds = Array.from(selectedUserIds);
      const result = await addUsersToRole({
        roleId,
        facilityId,
        userIds,
      });

      const successCount = result?.coreRoleAddUsers?.successCount ?? 0;
      const failedCount = result?.coreRoleAddUsers?.failedCount ?? 0;

      if (failedCount === 0) {
        toast.success(
          t.teamAndPermissions.roles.addUsersDialog.messages.success,
        );
      } else if (successCount > 0) {
        toast.warning(
          t.teamAndPermissions.roles.addUsersDialog.messages.partialSuccess
            .replace('{successCount}', successCount.toString())
            .replace('{totalCount}', (successCount + failedCount).toString()),
        );
      } else {
        toast.error(t.teamAndPermissions.roles.addUsersDialog.messages.error);
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['settings.teamPermissions.roleUsers'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['settings.teamPermissions.facilityRoles'],
        }),
      ]);

      setIsOpen(false);
      resetState();
    } catch {
      toast.error(t.teamAndPermissions.roles.addUsersDialog.messages.error);
    } finally {
      setIsAdding(false);
    }
  };

  if (allUsersAdded) {
    return (
      <Tooltip
        tip={t.teamAndPermissions.actions.allUsersAdded}
        className="ml-auto"
      >
        <div className="h-input inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm text-abs-white capitalize opacity-20">
          <LucidePlusCircle size={16} className="shrink-0" />
          {t.teamAndPermissions.actions.addUser}
        </div>
      </Tooltip>
    );
  }

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button className="ml-auto" icon={<LucidePlusCircle />}>
        {t.teamAndPermissions.actions.addUser}
      </Button>
      <Popover placement="bottom end" className="w-[377px]">
        <Col className="px-1.5 py-1">
          <div className="px-1">
            <SearchField
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={
                t.teamAndPermissions.roles.addUsersDialog.users
                  .searchPlaceholder
              }
              autoFocus
            />
          </div>

          <Row className="items-center justify-between px-1 pt-3 pb-0.5">
            <span className="text-sm font-semibold text-neutral/60 uppercase">
              {t.teamAndPermissions.roles.addUsersDialog.users.header} (
              {filteredUsers.length})
            </span>
            {selectedCount > 0 && (
              <Button
                variant="text"
                color="primary"
                size="small"
                className="h-auto py-0 text-sm"
                icon={<LucideXCircle className="size-4" />}
                onPress={handleClear}
              >
                {t.teamAndPermissions.roles.addUsersDialog.actions.clear} (
                {selectedCount})
              </Button>
            )}
          </Row>

          <Col className="max-h-[300px] gap-1 overflow-y-auto">
            {isLoading ? (
              <div className="py-4 text-center text-sm text-neutral/50">
                Loading...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-4 text-center text-sm text-neutral/50">
                No users found
              </div>
            ) : (
              filteredUsers.map((user) => (
                <Checkbox
                  key={user.id}
                  isSelected={selectedUserIds.has(user.id)}
                  onChange={() => handleToggleUser(user.id)}
                  className="rounded px-1.5 py-[5px] hover:bg-neutral/5"
                >
                  <Col>
                    <span className="text-sm leading-[22px]">{user.name}</span>
                    <span className="text-xs leading-normal text-neutral/60">
                      {user.email}
                    </span>
                  </Col>
                </Checkbox>
              ))
            )}
          </Col>

          <Row className="justify-end gap-2 border-t border-neutral/5 px-1.5 pt-3 pb-2">
            <Button
              variant="outline"
              color="neutral"
              onPress={handleDiscard}
              isDisabled={isAdding}
            >
              {t.teamAndPermissions.actions.discard}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleAddUsers}
              isDisabled={selectedCount === 0 || isAdding}
              isLoading={isAdding}
            >
              {t.teamAndPermissions.roles.addUsersDialog.actions.addUser.replace(
                '{count}',
                selectedCount.toString(),
              )}
            </Button>
          </Row>
        </Col>
      </Popover>
    </PopoverTrigger>
  );
};
